import { z } from "zod";

/**
 * Named value shapes the engine can check without the card author writing a regex.
 *
 * `email` is the one that matters most: it is what refuses a value that grew a second
 * address through concatenation after `@variable` substitution.
 */
export const VALUE_CONSTRAINT_FORMATS = ["email", "uri", "uuid", "date-time", "e164"] as const;
export const ValueConstraintFormatSchema = z.enum(VALUE_CONSTRAINT_FORMATS);
export type ValueConstraintFormat = z.infer<typeof ValueConstraintFormatSchema>;

/** Bounds the customer-supplied regex. A pattern is data, so it is capped like data. */
export const VALUE_CONSTRAINT_PATTERN_MAX_LENGTH = 512;

/** Bounds the enum. 256 members is a picker, more is a data set. */
export const VALUE_CONSTRAINT_ENUM_MAX_MEMBERS = 256;

/**
 * What a value at one parameter path — or in one card variable — is allowed to be.
 *
 * Every field is optional and every field is a NARROWING. A constraint can only
 * refuse; it can never widen what a policy already permits, and it never rewrites a
 * value. Refuse, never coerce.
 *
 * The whole object is additive: a card that declares no constraint behaves exactly as
 * it did before constraints existed.
 */
export const ValueConstraintSchema = z.object({
  /**
   * Anchored regular expression, applied to the whole value.
   *
   * Restricted to a backtracking-free subset — see `isRe2SafePattern`. A pattern
   * outside the subset is refused rather than compiled, because a card author is not
   * a trusted source of regular expressions.
   */
  pattern: z.string().min(1).max(VALUE_CONSTRAINT_PATTERN_MAX_LENGTH).optional(),
  /** Closed set of permitted values. Compared against the rendered value, exactly. */
  enum: z.array(z.string()).min(1).max(VALUE_CONSTRAINT_ENUM_MAX_MEMBERS).optional(),
  /** Maximum rendered length, in UTF-16 code units. */
  maxLength: z.number().int().positive().optional(),
  /** Named shape. Checked with an anchored, linear-time matcher. */
  format: ValueConstraintFormatSchema.optional()
});

export type ValueConstraint = z.infer<typeof ValueConstraintSchema>;
